import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { AttemptQuotaInfo } from '../types';
import { useTranslation } from '../../../i18n';

interface RetryAttemptButtonProps {
  scenarioId: string;
  quota?: AttemptQuotaInfo;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  language?: string;
}

export const RetryAttemptButton: React.FC<RetryAttemptButtonProps> = ({
  scenarioId,
  quota,
  size = 'md',
  language,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error] = useState<string | null>(null);

  const canRetry = quota ? quota.canRetry : true;
  const attemptsRemaining = quota ? quota.attemptsRemaining : undefined;

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canRetry || loading) return;

    setLoading(true);
    const effectiveLang = language || (typeof window !== 'undefined' ? sessionStorage.getItem('trainova_preferred_language') : null) || 'en';
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('trainova_preferred_language', effectiveLang);
    }

    try {
      const res = await api.post<any>(`/api/v1/simulations/${scenarioId}/attempts`, {
        language: effectiveLang,
      });
      const payload = res?.data ?? res;
      const newAttemptId = payload?.attempt?.id || payload?.attemptId || payload?.id;
      if (newAttemptId) {
        navigate(`/simulations/${scenarioId}/run?attemptId=${newAttemptId}&lang=${effectiveLang}&retry=true`);
        return;
      }
      const resumeId = (res as any)?.error?.attemptId || (res as any)?.activeAttempt?.id || payload?.error?.attemptId;
      if (resumeId) {
        navigate(`/simulations/${scenarioId}/run?attemptId=${resumeId}&lang=${effectiveLang}&retry=true`);
        return;
      }
      navigate(`/simulations/${scenarioId}/briefing?lang=${effectiveLang}&retry=true`);
    } catch (err: any) {
      const errPayload = err?.response?.data || err;
      const errCode = errPayload?.error?.code || errPayload?.code;
      if (errCode === 'ACTIVE_ATTEMPT_EXISTS' && (errPayload?.error?.attemptId || errPayload?.activeAttempt?.id)) {
        const resumeId = errPayload?.error?.attemptId || errPayload?.activeAttempt?.id;
        navigate(`/simulations/${scenarioId}/run?attemptId=${resumeId}&lang=${effectiveLang}&retry=true`);
        return;
      }
      navigate(`/simulations/${scenarioId}/briefing?lang=${effectiveLang}&retry=true`);
    } finally {
      setLoading(false);
    }
  };

  const paddingStyle =
    size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 22px' : '9px 16px';
  const fontSize = size === 'sm' ? '12px' : size === 'lg' ? '14.5px' : '13.5px';

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <button
        type="button"
        onClick={handleRetry}
        disabled={!canRetry || loading}
        title={
          !canRetry
            ? quota?.retryReason || t('attempts.action.quota_reached', undefined, 'Attempt quota reached')
            : attemptsRemaining !== undefined
            ? t('attempts.action.attempts_remaining', { count: attemptsRemaining }, `${attemptsRemaining} attempts remaining`)
            : t('attempts.action.retry_simulation', undefined, 'Retry Simulation')
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: paddingStyle,
          fontSize: fontSize,
          fontWeight: 600,
          fontFamily: 'var(--body, sans-serif)',
          borderRadius: '9px',
          border: canRetry ? '1px solid #3B82F6' : '1px solid var(--line, #E2E8F0)',
          background: canRetry
            ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
            : 'var(--surface, #F1F5F9)',
          color: canRetry ? '#FFFFFF' : 'var(--muted, #94A3B8)',
          cursor: canRetry && !loading ? 'pointer' : 'not-allowed',
          boxShadow: canRetry ? '0 2px 6px rgba(37, 99, 235, 0.25)' : 'none',
          transition: 'all 0.15s ease',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        {loading ? (
          <>
            <svg
              style={{ width: '15px', height: '15px', animation: 'spin 1s linear infinite' }}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{t('attempts.action.starting', undefined, 'Starting...')}</span>
          </>
        ) : (
          <>
            <svg
              style={{ width: '15px', height: '15px', flexShrink: 0 }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>{canRetry ? t('attempts.action.retry_attempt', undefined, 'Retry Attempt') : t('attempts.action.quota_exhausted', undefined, 'Quota Exhausted')}</span>
            {canRetry && attemptsRemaining !== undefined && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  color: '#FFFFFF',
                  padding: '2px 7px',
                  borderRadius: '12px',
                  marginLeft: '2px',
                }}
              >
                {attemptsRemaining} {t('attempts.action.attempts_left', undefined, 'left')}
              </span>
            )}
          </>
        )}
      </button>
      {error && (
        <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: 'var(--bad, #EF4444)' }}>
          {error}
        </p>
      )}
    </div>
  );
};
